import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { Tag } from 'lib/types/tags';
import { TAG_TYPES } from 'lib/constants';
import { TagsDisplay } from 'features/PostDetail/TagsDisplay';
import { useApi } from 'lib/contexts/ApplicationState';
import { ContentCopy } from '@mui/icons-material';
import { Method } from './AddDrawer';

interface Props {
  postIds: number[];
  copyTags: Tag[];
  method?: Method;
  onChange: (message?: string) => void;
  onCancel: () => void;
}

export const AddTags = ({
  postIds,
  copyTags,
  method,
  onChange,
  onCancel,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [tags, setTags] = useState<Tag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const disableSave = isEmpty(postIds) || isEmpty(tags) || isSaving;

  const getSaveLabel = () => {
    if (isSaving) return 'Saving...';

    if (isEmpty(postIds)) {
      return 'Select Posts';
    }
    if (isEmpty(tags)) {
      return 'Select Tags';
    }

    return 'Save';
  };

  const selectTag = (tag: Tag) => {
    setTags(tags.concat(tag));
  };

  const removeTag = (tag: Tag) => {
    setTags(tags.filter((t) => t.id !== tag.id));
  };

  const saveTags = async () => {
    setIsSaving(true);
    try {
      await API.createBulkTags({
        postIds,
        tagValues: tags.map((t) => ({
          id: t.tagValueId,
          tagTypeId: t.tagTypeId,
          value: t.value || t.tagValue?.value,
        })),
      });
      resetTags();
      onChange(`${postIds.length} posts updated successfully`);
    } catch (err) {
      enqueueSnackbar(`Error saving Tags: ${err}`, { variant: 'error' });
    }
    setIsSaving(false);
  };

  const resetTags = () => {
    setTags([]);
  };

  useEffect(() => {
    setTags(copyTags);
  }, [copyTags]);

  if (!method) return null;

  return (
    <div className="grid w-full pt-4">
      <h2 className="text-xl font-semibold mb-2">Add tags to selected posts</h2>
      {method === 'copy' && (
        <div>
          You can click the copy icon
          <span className="mx-2"><ContentCopy /></span>
          on a post to select tags to copy
        </div>
      )}
      <div className="overflow-auto">
        <TagsDisplay
          tags={tags}
          tagTypes={[TAG_TYPES.tag, TAG_TYPES.event]}
          onSelectTag={selectTag}
          onRemoveTag={removeTag}
        />
      </div>
      <div className="w-full flex gap-2 justify-end">
        <button
          type="button"
          className="btn-primary-outlined"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={saveTags}
          disabled={disableSave}
        >
          {getSaveLabel()}
        </button>
      </div>
    </div>
  );
};
